import React, { useEffect, useState, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { useAuth } from '../../hooks/useAuth'

const OAuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { refetchUser } = useAuth()
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing')
  const [message, setMessage] = useState('')
  const hasProcessed = useRef(false)

  useEffect(() => {
    // Prevent duplicate execution - only run once
    if (hasProcessed.current) {
      return
    }

    const handleOAuthCallback = async () => {
      // Mark as processed immediately to prevent re-runs
      hasProcessed.current = true

      // Check for OAuth success/error parameters
      const oauthSuccess = searchParams.get('oauth_success')
      const oauthError = searchParams.get('oauth_error')
      const provider = searchParams.get('provider')
      const action = searchParams.get('action')
      const jwtToken = searchParams.get('jwt_token')

      if (oauthError) {
        setStatus('error')
        setMessage(decodeURIComponent(oauthError))
        toast.error(`OAuth failed: ${decodeURIComponent(oauthError)}`)

        // Redirect to login after a delay
        setTimeout(() => {
          navigate('/login')
        }, 3000)
        return
      }

      if (oauthSuccess) {
        // If we have a JWT token, store it
        if (jwtToken) {
          localStorage.setItem('rml_jwt_token', jwtToken)
        }

        // Refetch user data to update auth context and redirect immediately
        try {
          await refetchUser()
          toast.success(`${provider ? provider.charAt(0).toUpperCase() + provider.slice(1) + ' ' : ''}login successful!`)

          // Redirect immediately to dashboard
          navigate('/dashboard', { replace: true })
        } catch (error) {
          console.error('Failed to fetch user after OAuth:', error)
          setStatus('error')
          setMessage('Authentication successful but failed to load user data')

          // Still redirect to login to try again
          setTimeout(() => {
            navigate('/login')
          }, 3000)
        }
        return
      }

      // No OAuth parameters found - might be a regular page load
      setStatus('error')
      setMessage('No OAuth result found')
      setTimeout(() => {
        navigate('/login')
      }, 2000)
    }

    handleOAuthCallback()
  }, [searchParams, navigate, refetchUser])

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return <LoadingSpinner size="lg" />
      case 'success':
        return (
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )
      case 'error':
        return (
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        )
    }
  }

  const getStatusTitle = () => {
    switch (status) {
      case 'processing':
        return 'Processing authentication...'
      case 'success':
        return 'Authentication successful!'
      case 'error':
        return 'Authentication failed'
    }
  }

  const getStatusDescription = () => {
    switch (status) {
      case 'processing':
        return 'Please wait while we complete your authentication.'
      case 'success':
        return message || 'You have been successfully authenticated. Redirecting to dashboard...'
      case 'error':
        return message || 'There was an error with your authentication. Redirecting to login...'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="max-w-md w-full"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* Status Icon */}
          <div className="flex justify-center mb-6">
            {getStatusIcon()}
          </div>

          {/* Status Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {getStatusTitle()}
          </h1>

          {/* Status Description */}
          <p className="text-gray-600 mb-6">
            {getStatusDescription()}
          </p>

          {/* Loading indicator for processing */}
          {status === 'processing' && (
            <div className="flex justify-center">
              <div className="animate-pulse flex space-x-1">
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              </div>
            </div>
          )}

          {/* Manual redirect button for errors */}
          {status === 'error' && (
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Go to Login
            </button>
          )}

          {/* Manual redirect button for success */}
          {status === 'success' && (
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Go to Dashboard
            </button>
          )}
        </div>
      </motion.div>
    </div>
  )
}

export default OAuthCallbackPage